import { useMemo } from 'react';
import { Order } from '@hero/schema';
import { CONFIG, TimelineNameId } from '@hero/core';
import { useOrderDestination } from './useOrderDestination';

export type TimelineStepStatus = 'completed' | 'in_progress' | 'upcoming';

export interface TimelineStep {
  /** The ID/name of this timeline step */
  stepId: TimelineNameId;
  /** Status of this timeline step */
  status: TimelineStepStatus;
  /** Whether this step has occurred */
  occurred: boolean;
  /** Whether this step is currently in progress */
  inProgress: boolean;
  /** The date when this step occurred (if it has occurred) */
  date?: Date;
  /** Translation key for the completed/default label (namespace-relative) */
  labelKey: string;
  /** Translation key for the in-progress/active label */
  activeLabelKey?: string;
}

export interface UseOrderTimelineOptions {
  /** The order to generate timeline for */
  order: Order;
  /** The type of user viewing the timeline (affects which timeline is shown) */
  displayForUser: 'customer' | 'carrier';
}

export interface UseOrderTimelineReturn {
  /** Array of timeline steps with their status */
  steps: TimelineStep[];
  /** Index of the currently active/in-progress step (-1 if none) */
  activeStepIndex: number;
}

/**
 * Hook to generate order timeline based on order data and timeline config
 *
 * @example
 * ```tsx
 * const { steps, activeStepIndex } = useOrderTimeline({
 *   order,
 *   displayForUser: 'customer'
 * });
 * ```
 */
export function useOrderTimeline({
  order,
  displayForUser,
}: UseOrderTimelineOptions): UseOrderTimelineReturn {
  // Get first pickup and last dropoff destinations
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);

  const timelineSteps = useMemo(() => {
    // Select the appropriate timeline based on user type
    const configTimeline =
      displayForUser === 'customer'
        ? CONFIG.order.orderTimelineConfig.customersTimeline
        : CONFIG.order.orderTimelineConfig.carriersTimeline;

    const steps: TimelineStep[] = [];

    // Pre-calculate destination IDs for pickup and dropoff
    const destinationIdMap = {
      pickup: firstPickup?.id,
      dropoff: lastDropoff?.id,
    };

    // Get the step type by finding which key in steps config matches this configStep
    const getStepType = (configStep: (typeof configTimeline)[number]): TimelineNameId => {
      const entries = Object.entries(CONFIG.order.orderTimelineConfig.steps) as Array<
        [TimelineNameId, typeof configStep]
      >;
      const found = entries.find(([_, step]) => step === configStep);
      return found ? found[0] : 'order_created'; // fallback
    };

    for (const configStep of configTimeline) {
      const stepType = getStepType(configStep);
      // Check if step has occurred by looking for any of the occurrence indicator events
      let occurred = false;
      let stepDate: Date | undefined;

      for (const eventType of configStep.occurrenceIndicators) {
        // Filter events by destination type if specified
        const events = order.timeline.filter(e => {
          if (e.event !== eventType) return false;

          // If destinationTargetType is specified, match against the specific destination ID
          if (configStep.destinationTargetType) {
            const targetDestinationId = destinationIdMap[configStep.destinationTargetType];
            if (!targetDestinationId) {
              return false; // No destination of this type exists
            }
            // Event must have a destinationId that matches the target destination
            return e.destinationId === targetDestinationId;
          }

          return true;
        });

        // Process events based on useLastOccurrenceDate
        if (events.length > 0) {
          occurred = true;
          const targetEvent = configStep.useLastOccurrenceDate
            ? events[events.length - 1]
            : events[0];

          if (!stepDate || configStep.useLastOccurrenceDate) {
            stepDate = targetEvent.date;
          }
        }
      }

      // Check if step is in progress
      // A step is in progress if the status matches AND the step hasn't occurred yet
      let inProgress = false;
      if (configStep.inProgressIndicators?.status && !occurred) {
        inProgress = configStep.inProgressIndicators.status.includes(order.status.order);
      }

      if (configStep.hideIfNotInProgress && !inProgress) {
        continue;
      }

      if (configStep.hideIfNotOccurred && !occurred) {
        continue;
      }

      // Determine step status
      let status: TimelineStepStatus;
      if (occurred) {
        status = 'completed';
      } else if (inProgress) {
        status = 'in_progress';
      } else {
        status = 'upcoming';
      }

      steps.push({
        stepId: stepType,
        status,
        occurred,
        inProgress,
        date: stepDate,
        labelKey: configStep.labelKey,
        activeLabelKey: configStep.activeLabelKey,
      });
    }

    return steps;
  }, [order, displayForUser, firstPickup?.id, lastDropoff?.id]);

  // Find the active step index (first in_progress step)
  const activeStepIndex = useMemo(() => {
    return timelineSteps.findIndex(step => step.status === 'in_progress');
  }, [timelineSteps]);

  return {
    steps: timelineSteps,
    activeStepIndex,
  };
}
